import { useEffect, useState, FC } from 'react'
import CategoryProductsContainer from '@/components/organisms/categoryProductsContainer'
import { Box, Flex } from '@/components/atoms/Grid'
import { Desktop, MediaContextProvider, Mobile } from '@/components/atoms/media'
import { Config } from '@/core/config'
import ContentCategoryMenu from '@/components/organisms/contentCategoryMenu'
import {
  findLastNonullSlugId,
  makeSlugPathFromCategories,
  makeSlugPathFromStringCategoryId,
  SlugPath
} from 'types/slugPath'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import Filter from '@/components/organisms/filter'
import { Capitalized } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import { useActiveFilterDispatch } from '@/providers/filterProvider/filtersProvider'
import ProductListingTitleCountInfo from '@/components/molecules/productListingTitleCountInfo'
import ProductListingPathHeader from '@/components/molecules/productListingPathHeader'
import {
  AdditionalInfoPageType,
  Category,
  Filter as FilterTypeGraphQl,
  GetInitialCategoryQuery,
  GetNextProductsForCategoryQuery,
  HomeBanner,
  ListingBanner,
  ListingProducts,
  Sdk
} from '@/network/graphql.g'
import { BridgeProduct, convertAPIProductToProduct } from 'types/bridgeProduct'
import type {
  ProductListingFetcher,
  ProductListingFetcherResponse
} from '@/components/organisms/productListing/productListingFetcher'
import {
  withFilterProvider,
  WithFilterProviderProps
} from '@/HOC/withFilterProvider'
import Cache from '@/utils/cache/redisServer'
import useGraphQl from 'hooks/useGraphQl'
import { RequestResponse, requestThrow401 } from '@/utils/graphQl'
import CategoryProductBanner from '@/components/organisms/categoryProductBanner'
import { CategoryType } from '@/types/categoryType'
import { makeFilterInputFromQuery } from '@/providers/filterProvider/parsingFilters'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import ProductListingDescription from '@/components/molecules/productListingDescription'
import { isInteger } from '@/utils/typeHelper'
import { getProductListingData } from '@/components/organisms/productListing/productListing'
import { useUser } from '@/providers/userProvider'
import { useAdditionalInfoProvider } from '@/providers/additionalInfoProvider'
import { useRouter } from 'next/router'
import { useMhub } from '@/providers/mhubProvider'
import { getCachableHeader } from '@/core/utils'
import useProductListing from 'hooks/useProductListing'
import { useSpeedCurve } from '@/core/speedCurve/useSpeedCurve'
import type { GetServerSidePropsContext } from 'next'
import { getMainData } from '@/utils/mainDataHelper'
import { SentryError } from '@/core/sentryError'
import { CacheType } from '@/utils/cache/pageDataCache'
import TopCategoriesSlider from '@/components/molecules/topCategoriesSlider'
import { setLocalDataIS } from '@/core/infiniteScroll'
import { validFor } from '../theme'
import styled from 'styled-components'
import { useDomain } from '@/providers/storeProvider'
import { SeoBreadcrumbList, SeoProductList } from '@/utils/seoData'

type CategoriesProps = {
  categoryProducts?: ListingProducts
  categoryTree?: Category
  slugPath: SlugPath
  listingBanners: ListingBanner[]
  categoryKey: string
  categoryProductBanners: HomeBanner[]
  mainCategoryBanners: HomeBanner[]
  categoryDescription: string
  filterId: string
  requestId: string
  isPreload: boolean
  page: number
} & WithFilterProviderProps

interface ListingProductsOld {
  __typename?: 'ListingProducts'
  filter: FilterTypeGraphQl[]
}

interface ResponseCarousel {
  __typename?: RequestResponse<GetNextProductsForCategoryQuery>
  mobileCategoriesCarousel: Category[]
}

interface ProductListDataObj {
  products?: BridgeProduct[]
  page?: number
}

interface ProductListData {
  categoryId?: string
  filterInput?: string
  productList?: ProductListDataObj[]
}

const Categories: FC<CategoriesProps> = ({
  slugPath,
  categoryProducts,
  categoryTree,
  listingBanners,
  categoryKey,
  categoryProductBanners,
  mainCategoryBanners,
  categoryDescription,
  filterId,
  requestId,
  isPreload,
  page
}) => {
  const { t } = useTranslation('common')
  const { isLoggedIn } = useUser()
  const slugPathValues = slugPath
    ? Object.values(slugPath).filter((value) => value)
    : []
  const category = slugPathValues[slugPathValues.length - 1]
  const isMain = categoryTree?.children.find((item) => item.id === category?.id)
  const router = useRouter()
  const [carouselTopCategories, setCarouselTopCategories] = useState<
    Category[]
  >([])

  const { setParameters } = useAdditionalInfoProvider()
  useEffect(() => {
    const filter = makeFilterInputFromQuery(router?.query)

    setParameters(AdditionalInfoPageType.Category, category?.id, filter)
    return () => {
      setParameters(null, null, null)
    }
  }, [category?.id, router?.asPath])

  const { setFilters } = useActiveFilterDispatch()
  const { requestNotifyError } = useGraphQl()
  const [count, setCount] = useState<number>(
    categoryProducts.totalProductsCount
  )

  const { subdomainName, domainName } = useDomain()
  const baseUrl = 'https://www.' + domainName + '.' + subdomainName

  useEffect(() => {
    setCount(categoryProducts?.totalProductsCount ?? 0)
  }, [categoryProducts?.totalProductsCount])

  const fetcher: ProductListingFetcher = async (
    page,
    filterInput,
    refresh,
    initialLoad = false
  ): Promise<ProductListingFetcherResponse> => {
    const useNextProductList = page > 1

    const result = await requestNotifyError(
      (sdk: Sdk) => ({
        method: !useNextProductList
          ? sdk.getFirstProductsForCategory
          : sdk.getNextProductsForCategory,
        variables: {
          limit: Config.perPage,
          id: category?.id,
          page: page,
          filter: filterInput,
          withFilterAndProductsCount: refresh
        },
        requestHeaders: getCachableHeader(filterInput, isLoggedIn)
      }),
      true
    )
    if (!result.success) {
      throw (
        result.error ??
        new SentryError('Category product listing', result.response)
      )
    }

    const { products, totalPagesCount, totalProductsCount } =
      result.response?.categoryProducts ?? {}

    if (!useNextProductList) {
      const response = result.response?.categoryProducts as ListingProductsOld
      const { filter } = response ?? {}

      if (filter) {
        setFilters([...filter])
      }
    }

    setCarouselTopCategoriesData(result)

    !initialLoad && pushSimpleCategoryDetailPage(category, page)

    refresh && setCount(totalProductsCount)

    const productList =
      products?.map((product) => convertAPIProductToProduct(product)) ?? []

    setLocalDataIS(productList, page, filterInput, category.id)

    return {
      products: productList,
      totalPagesCount
    }
  }

  const setCarouselTopCategoriesData = (
    result: RequestResponse<GetNextProductsForCategoryQuery>
  ) => {
    const response = result.response as unknown as ResponseCarousel

    const cachedCarouselData = localStorage.getItem(
      `mobileCategoriesCarousel_${category?.id}`
    )

    if (cachedCarouselData) {
      setCarouselTopCategories(JSON.parse(cachedCarouselData))
      return
    }

    const mobileCategoriesCarousel = response.mobileCategoriesCarousel

    if (mobileCategoriesCarousel) {
      setCarouselTopCategories(mobileCategoriesCarousel)

      localStorage.setItem(
        `mobileCategoriesCarousel_${category?.id}`,
        JSON.stringify(mobileCategoriesCarousel)
      )
    }
  }

  const productListKey = 'productList'

  const getProducstFromStorage = (): ProductListData => {
    return localStorage.getItem(productListKey)
      ? JSON.parse(localStorage.getItem(productListKey))
      : null
  }

  const getProducstFromStorageByPage = (page: number): ProductListData => {
    const productList: ProductListData = getProducstFromStorage()

    if (!getProducstFromStorage) {
      return null
    }

    const productListPage: ProductListDataObj[] = []

    productList.productList.forEach((productsObj) => {
      if (productsObj.page <= page) {
        productListPage.push(productsObj)
      }
    })

    productList.productList = productListPage

    return productList
  }

  const { data } = useProductListing({
    totalPagesCount: categoryProducts.totalPagesCount,
    totalProductsCount: categoryProducts.totalProductsCount,
    id: category?.id,
    listingBanners,
    products: categoryProducts.products,
    banners: isMain ? mainCategoryBanners : null,
    type: isMain ? CategoryType.maybeInterestedIn : undefined,
    filterId,
    requestId,
    isPreload,
    fetcher
  })

  const { pushCategoryDetailPage, pushSimpleCategoryDetailPage } = useMhub()
  useEffect(() => {
    pushCategoryDetailPage(category)
  }, [category])

  useSpeedCurve(
    ['3', '20', '101', '112', '719'].includes(category?.id)
      ? {
          label: 'Category'
        }
      : { label: 'Category Nested' },
    [router?.asPath]
  )

  useEffect(() => {
    if (!isPreload) {
      const productListData: ProductListData =
        getProducstFromStorageByPage(page)

      if (productListData) {
        const productList = productListData.productList.flatMap(
          (page) => page.products
        )

        data.products = productList
      }
    }
  }, [category?.id, router.asPath])

  const hasCategoryTreeChildren = categoryTree?.children.length > 0

  return (
    <StyledPageContentWrapper>
      <SeoBreadcrumbList
        slugPathValues={slugPathValues}
        baseUrl={baseUrl}
      ></SeoBreadcrumbList>

      <SeoProductList
        productList={data?.products}
        baseUrl={baseUrl}
      ></SeoProductList>

      <Box key={categoryKey}>
        {!isMain && (
          <CategoryProductBanner
            banners={categoryProductBanners}
            preload={{ desktop: true, tablet: false, mobile: false }}
          />
        )}

        <ProductListingPathHeader
          title={category?.title}
          paths={slugPathValues}
        />

        {!isMain ? (
          <>
            {categoryProducts && (
              <ProductListingTitleCountInfo
                title={category?.title}
                additionalInfo={t('Common.Products', {
                  count
                })}
              />
            )}
            <ProductListingDescription
              description={categoryDescription as string}
              enableReadMore={true}
            />
            <Filter
              subcategories={categoryTree.children}
              mb={{ desktop: 30, tablet: 30, mobile: 0 }}
              slugPath={slugPath}
            />
            <MediaContextProvider>
              <Mobile>
                <TopCategoriesSlider
                  carouselTopCategories={carouselTopCategories}
                />
              </Mobile>
            </MediaContextProvider>
          </>
        ) : (
          <ProductListingDescription
            description={categoryDescription as string}
            enableReadMore={true}
          />
        )}
        <Flex mt={isMain ? { desktop: 40, tablet: 20, mobile: 12 } : 0}>
          <MediaContextProvider>
            <Desktop>
              {hasCategoryTreeChildren ? (
                <Capitalized opacity={0.5} mb={20}>
                  {t('Category.title').toUpperCase()}
                </Capitalized>
              ) : null}
              <ContentCategoryMenu
                slugPath={slugPath}
                mr={hasCategoryTreeChildren ? 46 : 0}
                width={hasCategoryTreeChildren ? 200 : 0}
                subcategories={
                  categoryTree.children.find(
                    ({ id }) => id === slugPath?.categoryId?.id
                  )?.children
                }
              />
            </Desktop>
          </MediaContextProvider>
          <CategoryProductsContainer
            productListingFetcher={fetcher}
            data={data}
            showFilteredBy={!isMain}
            showPager={false}
            list={`list.category.${category?.id}-${category?.slug}`}
          />
        </Flex>
      </Box>
    </StyledPageContentWrapper>
  )
}

const StyledPageContentWrapper = styled(PageContentWrapper)`
  min-height: 800px;
  margin-bottom: 50px;
  ${validFor.mobile`
    padding: 0 10px;
  `}
`

const NotFound = {
  notFound: true
}

const getCommonProps = (
  context: GetServerSidePropsContext,
  props?: { requestId: string }
): { requestId: string } => ({
  requestId: props?.requestId ?? null
})

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withMainNavigation,
    withStore,
    session
  }): Promise<GetServerSideProps<CategoriesProps>> => {
    const productListingData = getProductListingData(context)

    if ('redirect' in productListingData) {
      return {
        redirect: productListingData.redirect
      }
    }
    const { page, limit, filterInput, filterId, requestId, isPreload } =
      productListingData

    const slugs = context.params?.slug as string[]
    let data: GetInitialCategoryQuery
    let slugPath: SlugPath
    const firstSlug = slugs[0]
    let categoriesTree: Category
    let categoryDescription = ''
    const isCategoryTreeInCache = await Cache.hasCategoriesTree(context)

    if (!isInteger(firstSlug)) {
      if (isCategoryTreeInCache) {
        categoriesTree = await Cache.getCategoriesTree(context)
      } else {
        const { success, response } = await requestThrow401({
          method: sdk.getCategoriesTreeJson,
          requestHeaders: {
            'x-cachable': '1'
          }
        })
        if (!success) {
          return NotFound
        }
        categoriesTree = JSON.parse(response?.categoriesTreeJson) as Category
        await Cache.setCategoriesTree(categoriesTree, context)
      }
      slugPath = makeSlugPathFromStringCategoryId(
        categoriesTree,
        context.params.slug as string[]
      )

      const categoryId = findLastNonullSlugId(slugPath)
      if (!isInteger(categoryId)) {
        return NotFound
      }

      const { success, response } = await requestThrow401({
        method: sdk.getProductsForCategory,
        variables: {
          limit,
          id: categoryId,
          filter: filterInput,
          page,
          withMainNavigation,
          withStore
        },
        successPredicate: (response) =>
          !!(response?.categoryProducts && response?.listingBannersForCategory),
        requestHeaders: getCachableHeader(filterInput, session?.isLoggedIn)
      })
      if (!success) {
        return NotFound
      }
      data = {
        seoTags: response?.seoTags,
        categoryProducts: response?.categoryProducts,
        listingBannersForCategory: response?.listingBannersForCategory,
        homeBannersForHomeBannerCategory:
          response?.homeBannersForHomeBannerCategory,
        homeBannersForCategory: response?.homeBannersForCategory,
        mainNavigationJson: response?.mainNavigationJson ?? null,
        store: response?.store ?? null,
        premiumManufacturers: response.premiumManufacturers ?? null
      }
      categoryDescription = response?.category?.description
    } else {
      const { success, response } = await requestThrow401({
        method: sdk.getInitialCategory,
        variables: {
          id: firstSlug.toString(),
          limit,
          filter: filterInput,
          page,
          withCategoriesTree: !isCategoryTreeInCache,
          withMainNavigation,
          withStore
        },
        successPredicate: (response) =>
          !!(
            response?.categoryProducts &&
            response?.listingBannersForCategory &&
            response?.category &&
            (isCategoryTreeInCache || response?.categoriesTreeJson)
          ),
        requestHeaders: getCachableHeader(filterInput, session?.isLoggedIn)
      })
      if (!success) {
        return NotFound
      }
      if (isCategoryTreeInCache) {
        categoriesTree = await Cache.getCategoriesTree(context)
      } else {
        categoriesTree = JSON.parse(response?.categoriesTreeJson) as Category
        await Cache.setCategoriesTree(categoriesTree, context)
      }

      slugPath = makeSlugPathFromCategories(
        response?.category.traversablePath.reverse()
      )

      data = response
      categoryDescription = response?.category?.description
    }

    if (!data) {
      return NotFound
    }

    const key = (context.params.slug as string[])?.join('/')

    if (data?.seoTags) {
      data.seoTags.image =
        data?.categoryProducts.products?.find((item) => item)?.mainImage?.url ??
        null
    }

    const getCategoryWithValidChildren = (
      category: Category,
      ids: string[],
      level
    ) => {
      const localIds = [...ids]
      const id = localIds.shift()
      return {
        ...category,
        ...{
          children:
            category?.id === id || level === 2
              ? category?.children
                  ?.filter((item) => level + 1 > 1 || item.id === localIds?.[0])
                  .map((child) =>
                    getCategoryWithValidChildren(child, localIds, level + 1)
                  ) ?? []
              : []
        }
      }
    }

    const isMain = () => {
      const slugPathValues = Object.values(slugPath).filter((value) => value)
      const category = slugPathValues[slugPathValues.length - 1]
      return category?.id
        ? !!categoriesTree?.children.find((item) => item.id === category.id)
        : false
    }
    const filter = isMain()
      ? []
      : isPreload
      ? data?.categoryProducts?.filter?.map((item) => ({
          filterType: item.filterType,
          filterItems:
            item.filterItems?.map((section) => ({
              ...section,
              ...{ items: section.items?.slice(0, 1) ?? [] }
            })) ?? [],
          priceRange: item.priceRange
        })) ?? null
      : data?.categoryProducts.filter ?? null
    return {
      props: {
        ...{
          mainData: getMainData(data),
          seoTags: data?.seoTags,
          categoryKey: key,
          categoryProducts: data?.categoryProducts,
          categoryTree: getCategoryWithValidChildren(
            categoriesTree,
            [
              categoriesTree?.id,
              slugPath.categoryId?.id,
              slugPath.subcategoryId?.id,
              slugPath.itemId?.id,
              slugPath.subItemId?.id,
              slugPath.specialSubItemId?.id
            ].filter(Boolean),
            0
          ),
          listingBanners: data?.listingBannersForCategory,
          filter,
          slugPath,
          categoryProductBanners:
            data?.homeBannersForHomeBannerCategory ?? null,
          mainCategoryBanners: data?.homeBannersForCategory ?? null,
          categoryDescription: categoryDescription ?? '',
          filterId,
          isPreload,
          disableDaktela: true,
          page: page
        },
        ...getCommonProps(context, { requestId })
      }
    }
  },
  {
    cacheType: CacheType.Category,
    getCommonProps,
    onlyTopNavigation: false
  }
)

export default withFilterProvider(Categories)
